import { useState } from "react";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Edit, Save, X, ChevronDown, ChevronUp, AlertTriangle, Clock, CheckCircle, Send } from "lucide-react";
import { format } from "date-fns";
import { 
  useAllOutageNotifications, 
  useCreateOutageNotification, 
  useUpdateOutageNotification, 
  useDeleteOutageNotification,
  OutageNotification 
} from "@/hooks/use-outage-notifications";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";

const OUTAGE_TYPES = [
  { value: "scheduled_maintenance", label: "Scheduled Maintenance" },
  { value: "unplanned_outage", label: "Unplanned Outage" },
  { value: "degraded_performance", label: "Degraded Performance" },
  { value: "partial_outage", label: "Partial Outage" },
];

const SEVERITY_LEVELS = [
  { value: "low", label: "Low", color: "bg-muted text-muted-foreground" },
  { value: "medium", label: "Medium", color: "bg-yellow-500 text-black" },
  { value: "high", label: "High", color: "bg-orange-500 text-white" },
  { value: "critical", label: "Critical", color: "bg-destructive text-destructive-foreground" },
];

const STATUS_OPTIONS = [
  { value: "scheduled", label: "Scheduled" },
  { value: "in_progress", label: "In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "cancelled", label: "Cancelled" },
];

export default function OutageManagement() {
  const { toast } = useToast();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newService, setNewService] = useState("");
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [showResolutionDialog, setShowResolutionDialog] = useState(false);
  const [pendingSave, setPendingSave] = useState(false);
  
  const [formData, setFormData] = useState<Partial<OutageNotification>>({
    title: "",
    description: "",
    outage_type: "scheduled_maintenance",
    severity: "medium",
    status: "scheduled",
    scheduled_start: null,
    scheduled_end: null,
    affected_services: [],
    is_published: false,
    resolution_notes: "",
  });

  const { data: outages, isLoading } = useAllOutageNotifications();
  const createMutation = useCreateOutageNotification();
  const updateMutation = useUpdateOutageNotification();
  const deleteMutation = useDeleteOutageNotification();

  // Filter outages into live and resolved
  const liveOutages = outages?.filter(o => 
    o.status === 'scheduled' || o.status === 'in_progress'
  ) || [];
  
  const resolvedOutages = outages?.filter(o => 
    o.status === 'resolved' || o.status === 'cancelled'
  ) || [];

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      outage_type: "scheduled_maintenance",
      severity: "medium",
      status: "scheduled",
      scheduled_start: null,
      scheduled_end: null,
      affected_services: [],
      is_published: false,
      resolution_notes: "",
    });
    setIsCreating(false);
    setEditingId(null);
    setNewService("");
  };

  const handleEdit = (outage: OutageNotification) => {
    setFormData(outage);
    setEditingId(outage.id);
  };

  const handleSave = async () => {
    // Check if this is a new published outage or resolving an existing outage
    const isNewPublish = !editingId && formData.is_published;
    const isResolving = editingId && (formData.status === 'resolved' || formData.status === 'cancelled');
    
    // Get the existing outage to check previous status
    const existingOutage = editingId ? outages?.find(o => o.id === editingId) : null;
    const wasNotifiedAlready = existingOutage?.notifications_sent_at;
    const wasResolvedAlready = existingOutage?.resolution_sent_at;
    
    // Show confirmation dialog if publishing or resolving (and not already notified)
    if ((isNewPublish || (isResolving && wasNotifiedAlready && !wasResolvedAlready))) {
      setPendingSave(true);
      if (isNewPublish) {
        setShowAlertDialog(true);
      } else {
        setShowResolutionDialog(true);
      }
      return;
    }
    
    // Otherwise save directly
    await performSave(false);
  };

  const performSave = async (sendNotifications: boolean) => {
    setPendingSave(false);
    setShowAlertDialog(false);
    setShowResolutionDialog(false);
    
    if (editingId) {
      updateMutation.mutate(
        { id: editingId, data: formData },
        {
          onSuccess: async (data) => {
            toast({ title: "Outage notification updated successfully" });
            
            // Send notifications if requested and conditions are met
            if (sendNotifications) {
              const wasResolved = formData.status === 'resolved' || formData.status === 'cancelled';
              await sendOutageNotification(editingId, wasResolved ? 'resolved' : 'alert');
            }
            
            resetForm();
          },
          onError: (error: any) => {
            toast({ title: "Error updating outage notification", description: error.message, variant: "destructive" });
          },
        }
      );
    } else {
      createMutation.mutate(formData, {
        onSuccess: async (data, variables) => {
          toast({ title: "Outage notification created successfully" });
          
          // Send notifications if requested and published
          if (sendNotifications && formData.is_published) {
            // Need to get the created outage ID - refetch to get it
            const { data: createdOutages } = await supabase
              .from('outage_notifications')
              .select('id')
              .eq('title', formData.title)
              .order('created_at', { ascending: false })
              .limit(1);
            
            if (createdOutages && createdOutages.length > 0) {
              await sendOutageNotification(createdOutages[0].id, 'alert');
            }
          }
          
          resetForm();
        },
        onError: (error: any) => {
          toast({ title: "Error creating outage notification", description: error.message, variant: "destructive" });
        },
      });
    }
  };

  const sendOutageNotification = async (outageId: string, notificationType: 'alert' | 'resolved') => {
    try {
      const { data, error } = await supabase.functions.invoke('send-outage-notification', {
        body: { outageId, notificationType }
      });

      if (error) {
        toast({ 
          title: "Failed to send notifications", 
          description: error.message, 
          variant: "destructive" 
        });
      } else if (data?.alreadySent) {
        toast({ 
          title: "Notifications already sent", 
          description: `${notificationType === 'alert' ? 'Alert' : 'Resolution'} notifications were already sent for this outage`,
        });
      } else {
        toast({ 
          title: "Notifications sent successfully", 
          description: `Sent ${data?.inAppNotifications || 0} in-app and ${data?.emailsSent || 0} email notifications`
        });
      }
    } catch (error: any) {
      console.error('Error sending outage notification:', error);
      toast({ 
        title: "Failed to send notifications", 
        description: error.message, 
        variant: "destructive" 
      });
    }
  };

  const handleDelete = (id: string) => {
    if (confirm("Are you sure you want to delete this outage notification?")) {
      deleteMutation.mutate(id, {
        onSuccess: () => {
          toast({ title: "Outage notification deleted successfully" });
        },
        onError: (error: any) => {
          toast({ title: "Error deleting outage notification", description: error.message, variant: "destructive" });
        },
      });
    }
  };

  const addService = () => {
    if (newService.trim() && formData.affected_services) {
      setFormData({
        ...formData,
        affected_services: [...formData.affected_services, newService.trim()],
      });
      setNewService("");
    }
  };

  const removeService = (index: number) => {
    if (formData.affected_services) {
      setFormData({
        ...formData,
        affected_services: formData.affected_services.filter((_, i) => i !== index),
      });
    }
  };

  const getSeverityBadge = (severity: string) => {
    const config = SEVERITY_LEVELS.find(s => s.value === severity);
    return <Badge className={config?.color}>{config?.label || severity}</Badge>;
  };

  const headerActions = (
    <Button 
      variant="outline"
      onClick={() => setIsCreating(true)} 
      disabled={isCreating || editingId !== null}
      className="bg-white border-black text-black hover:bg-slate-50"
    >
      <Plus className="h-4 w-4 mr-2" />
      New Outage Notification
    </Button>
  );

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader 
            icon={AlertTriangle}
            title="Manage Outage Notifications"
            description="Create and manage service outage notifications"
            actions={headerActions}
          />
          <div className="p-4 sm:p-6 lg:p-12 xl:px-16" style={{ paddingTop: "8.5rem" }}>
            <div className="w-full max-w-none">

            {(isCreating || editingId) && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>{editingId ? "Edit Outage Notification" : "Create New Outage Notification"}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title">Title</Label>
                    <Input
                      id="title"
                      placeholder="e.g., Scheduled Database Maintenance"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Details about the outage or maintenance"
                      value={formData.description || ""}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="outage_type">Type</Label>
                      <Select
                        value={formData.outage_type}
                        onValueChange={(value) => setFormData({ ...formData, outage_type: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {OUTAGE_TYPES.map(type => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="severity">Severity</Label>
                      <Select
                        value={formData.severity}
                        onValueChange={(value) => setFormData({ ...formData, severity: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {SEVERITY_LEVELS.map(level => (
                            <SelectItem key={level.value} value={level.value}>
                              {level.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="status">Status</Label>
                    <Select
                      value={formData.status}
                      onValueChange={(value) => setFormData({ ...formData, status: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(status => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {(formData.status === 'resolved' || formData.status === 'cancelled') && (
                    <div className="space-y-2">
                      <Label htmlFor="resolution_notes">Resolution Notes</Label>
                      <Textarea
                        id="resolution_notes"
                        placeholder="Add details about how this was resolved..."
                        value={formData.resolution_notes || ""}
                        onChange={(e) => setFormData({ ...formData, resolution_notes: e.target.value })}
                        rows={3}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="scheduled_start">Scheduled Start</Label>
                      <Input
                        id="scheduled_start"
                        type="datetime-local"
                        value={formData.scheduled_start ? new Date(formData.scheduled_start).toISOString().slice(0, 16) : ""}
                        onChange={(e) => setFormData({ ...formData, scheduled_start: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="scheduled_end">Scheduled End</Label>
                      <Input
                        id="scheduled_end"
                        type="datetime-local"
                        value={formData.scheduled_end ? new Date(formData.scheduled_end).toISOString().slice(0, 16) : ""}
                        onChange={(e) => setFormData({ ...formData, scheduled_end: e.target.value ? new Date(e.target.value).toISOString() : null })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Affected Services</Label>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Add a service name"
                        value={newService}
                        onChange={(e) => setNewService(e.target.value)}
                        onKeyPress={(e) => e.key === "Enter" && addService()}
                      />
                      <Button type="button" onClick={addService}>Add</Button>
                    </div>
                    {formData.affected_services && formData.affected_services.length > 0 && (
                      <ul className="space-y-2 mt-2">
                        {formData.affected_services.map((service, idx) => (
                          <li key={idx} className="flex items-center justify-between bg-muted p-2 rounded">
                            <span>{service}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeService(idx)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      id="is_published"
                      checked={formData.is_published}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, is_published: checked })
                      }
                    />
                    <Label htmlFor="is_published">Publish immediately</Label>
                  </div>

                  <div className="flex gap-2">
                    <Button 
                      variant="outline"
                      onClick={handleSave}
                      className="bg-white border-black text-black hover:bg-slate-50"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <Button variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Outages List with Tabs */}
            {isLoading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Loading outage notifications...</p>
              </div>
            ) : (
              <Tabs defaultValue="live" className="w-full">
                <TabsList>
                  <TabsTrigger value="live">
                    Live ({liveOutages.length})
                  </TabsTrigger>
                  <TabsTrigger value="resolved">
                    Resolved ({resolvedOutages.length})
                  </TabsTrigger>
                </TabsList>

                {/* Live Outages Tab */}
                <TabsContent value="live">
                  {liveOutages.length > 0 ? (
                    <Accordion type="single" collapsible value={expandedId || undefined} onValueChange={setExpandedId}>
                      {liveOutages.map((outage) => (
                        <AccordionItem key={outage.id} value={outage.id}>
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center justify-between gap-3 flex-1 mr-2">
                              <span className="font-medium text-base">{outage.title}</span>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {getSeverityBadge(outage.severity)}
                                {outage.is_published ? (
                                  <Badge variant="success">Published</Badge>
                                ) : (
                                  <Badge variant="secondary">Draft</Badge>
                                )}
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3 pt-4">
                              {/* Details - single line */}
                              <div className="text-sm flex items-center gap-2 flex-wrap">
                                <span className="text-muted-foreground">Type:</span>
                                <span className="font-medium">{OUTAGE_TYPES.find(t => t.value === outage.outage_type)?.label}</span>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground">Status:</span>
                                <Badge variant="outline">{STATUS_OPTIONS.find(s => s.value === outage.status)?.label}</Badge>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground">Severity:</span>
                                {getSeverityBadge(outage.severity)}
                              </div>

                              {/* Schedule - single line with arrow */}
                              {(outage.scheduled_start || outage.scheduled_end) && (
                                <div className="text-sm flex items-center gap-2 flex-wrap">
                                  <span className="text-muted-foreground">Schedule:</span>
                                  {outage.scheduled_start && (
                                    <span className="font-medium">{format(new Date(outage.scheduled_start), "PPp")}</span>
                                  )}
                                  {outage.scheduled_start && outage.scheduled_end && (
                                    <span className="text-muted-foreground">→</span>
                                  )}
                                  {outage.scheduled_end && (
                                    <span className="font-medium">{format(new Date(outage.scheduled_end), "PPp")}</span>
                                  )}
                                </div>
                              )}

                              {/* Description - left aligned */}
                              {outage.description && (
                                <div className="text-sm text-left">
                                  <span className="text-muted-foreground">Description: </span>
                                  <span className="text-muted-foreground">{outage.description}</span>
                                </div>
                              )}

                              {/* Affected Services - inline */}
                              {outage.affected_services && outage.affected_services.length > 0 && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm text-muted-foreground">Affected:</span>
                                  {outage.affected_services.map((service, idx) => (
                                    <Badge key={idx} variant="secondary">
                                      {service}
                                    </Badge>
                                  ))}
                                </div>
                              )}

                              <Separator />
                              
                              {/* Actions */}
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(outage)}
                                  disabled={isCreating || editingId !== null}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(outage.id)}
                                  disabled={isCreating || editingId !== null}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No live outages</p>
                      <p className="text-sm mt-2">All systems operational</p>
                    </div>
                  )}
                </TabsContent>

                {/* Resolved Outages Tab */}
                <TabsContent value="resolved">
                  {resolvedOutages.length > 0 ? (
                    <Accordion type="single" collapsible value={expandedId || undefined} onValueChange={setExpandedId}>
                      {resolvedOutages.map((outage) => (
                        <AccordionItem key={outage.id} value={outage.id}>
                          <AccordionTrigger className="hover:no-underline">
                            <div className="flex items-center justify-between gap-3 flex-1 mr-2">
                              <div className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-success flex-shrink-0" />
                                <span className="font-medium text-base text-muted-foreground">{outage.title}</span>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                {getSeverityBadge(outage.severity)}
                                {outage.is_published ? (
                                  <Badge variant="success">Published</Badge>
                                ) : (
                                  <Badge variant="secondary">Draft</Badge>
                                )}
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="space-y-3 pt-4">
                              {/* Details - single line */}
                              <div className="text-sm flex items-center gap-2 flex-wrap">
                                <span className="text-muted-foreground">Type:</span>
                                <span className="font-medium">{OUTAGE_TYPES.find(t => t.value === outage.outage_type)?.label}</span>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground">Status:</span>
                                <Badge variant="outline">{STATUS_OPTIONS.find(s => s.value === outage.status)?.label}</Badge>
                                <span className="text-muted-foreground">•</span>
                                <span className="text-muted-foreground">Severity:</span>
                                {getSeverityBadge(outage.severity)}
                              </div>

                              {/* Schedule - single line with arrow */}
                              {(outage.scheduled_start || outage.scheduled_end) && (
                                <div className="text-sm flex items-center gap-2 flex-wrap">
                                  <span className="text-muted-foreground">Schedule:</span>
                                  {outage.scheduled_start && (
                                    <span className="font-medium">{format(new Date(outage.scheduled_start), "PPp")}</span>
                                  )}
                                  {outage.scheduled_start && outage.scheduled_end && (
                                    <span className="text-muted-foreground">→</span>
                                  )}
                                  {outage.scheduled_end && (
                                    <span className="font-medium">{format(new Date(outage.scheduled_end), "PPp")}</span>
                                  )}
                                </div>
                              )}

                              {/* Description - left aligned */}
                              {outage.description && (
                                <div className="text-sm text-left">
                                  <span className="text-muted-foreground">Description: </span>
                                  <span className="text-muted-foreground">{outage.description}</span>
                                </div>
                              )}

                              {/* Resolution Notes - highlighted */}
                              {outage.resolution_notes && (
                                <div className="text-sm text-left bg-muted/50 p-3 rounded">
                                  <span className="text-muted-foreground font-medium">Resolution Notes: </span>
                                  <span>{outage.resolution_notes}</span>
                                </div>
                              )}

                              {/* Affected Services - inline */}
                              {outage.affected_services && outage.affected_services.length > 0 && (
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm text-muted-foreground">Affected:</span>
                                  {outage.affected_services.map((service, idx) => (
                                    <Badge key={idx} variant="secondary">
                                      {service}
                                    </Badge>
                                  ))}
                                </div>
                              )}

                              <Separator />
                              
                              {/* Actions */}
                              <div className="flex gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleEdit(outage)}
                                  disabled={isCreating || editingId !== null}
                                >
                                  <Edit className="h-4 w-4 mr-2" />
                                  Edit
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(outage.id)}
                                  disabled={isCreating || editingId !== null}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete
                                </Button>
                              </div>
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>No resolved outages</p>
                      <p className="text-sm mt-2">Resolved notifications will appear here</p>
                    </div>
                  )}
                </TabsContent>
              </Tabs>
            )}

            </div>
          </div>
        </main>
      </div>
      
      {/* Alert Dialog for Publishing New Outage */}
      <AlertDialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Outage Alert Notifications?</AlertDialogTitle>
            <AlertDialogDescription>
              This will send notifications to all active users about this outage. 
              Users subscribed to outage notifications will receive emails.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => performSave(false)}>
              Save Without Notifications
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => performSave(true)}>
              <Send className="h-4 w-4 mr-2" />
              Send Notifications
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Alert Dialog for Resolving Outage */}
      <AlertDialog open={showResolutionDialog} onOpenChange={setShowResolutionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send Resolution Notifications?</AlertDialogTitle>
            <AlertDialogDescription>
              This will notify users that the outage has been resolved. 
              Users subscribed to outage notifications will receive emails.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => performSave(false)}>
              Save Without Notifications
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => performSave(true)}>
              <Send className="h-4 w-4 mr-2" />
              Send Notifications
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
}
